import type {
  Applicant,
  AssetCategory,
  AssetItem,
  Borrower,
  CaseData,
  CaseSummary,
  DebtCategory,
  DebtItem,
  Guarantor,
  IncomeCategory,
  IncomeItem,
  PropertyData,
} from "./caseSchema";

export type ReportSections = {
  meta: {
    generatedAt: string;
    guarantorIncluded: boolean;
  };
  applicant: Applicant;
  property: PropertyData;
  borrower: {
    totals: {
      monthlyIncomeYuan: number;
      assetYuan: number;
      monthlyDebtYuan: number;
      debtBalanceYuan: number;
    };
    incomes: IncomeItem[];
    assets: AssetItem[];
    debts: DebtItem[];
  };
  guarantor?: {
    totals: {
      monthlyIncomeYuan: number;
      assetYuan: number;
      monthlyDebtYuan: number;
      debtBalanceYuan: number;
    };
    incomes: IncomeItem[];
    assets: AssetItem[];
    debts: DebtItem[];
  };
  totals: CaseSummary;
};

type UnknownRecord = Record<string, unknown>;

declare global {
  interface Window {
    BLIncomeContract?: {
      getPersonIncomeSources(input: unknown, role: "BORROWER" | "GUARANTOR"): Array<{ type:string; normalizedMonthlyAmount:number|null; metadata?:UnknownRecord }>;
    };
  }
}

const CHINESE_DIGITS: Record<string, number> = {
  零: 0,
  一: 1,
  二: 2,
  兩: 2,
  三: 3,
  四: 4,
  五: 5,
  六: 6,
  七: 7,
  八: 8,
  九: 9,
};

const SMALL_UNITS: Record<string, number> = {
  十: 10,
  百: 100,
  千: 1000,
};

function containsChineseNumberTokens(s: string): boolean {
  return /[零一二兩三四五六七八九十百千]/.test(s);
}

function parseChineseUnder10000(segment: string): number {
  if (!segment) return 0;

  let total = 0;
  let currentDigit: number | null = null;

  for (let i = 0; i < segment.length; i++) {
    const ch = segment[i]!;

    if (ch in CHINESE_DIGITS) {
      currentDigit = CHINESE_DIGITS[ch]!;
      continue;
    }

    if (ch in SMALL_UNITS) {
      const unit = SMALL_UNITS[ch]!;
      const digit = currentDigit ?? 1;
      total += digit * unit;
      currentDigit = null;
      continue;
    }
  }

  if (currentDigit !== null) total += currentDigit;
  return total;
}

function parseChineseNumber(text: string): number {
  let s = text;
  if (!s) return 0;

  // 億
  const yiParts = s.split("億");
  if (yiParts.length === 2) {
    return parseChineseNumber(yiParts[0] ?? "") * 100_000_000 + parseChineseNumber(yiParts[1] ?? "");
  }

  // 萬
  const wanParts = s.split("萬");
  if (wanParts.length === 2) {
    return parseChineseUnder10000(wanParts[0] ?? "") * 10_000 + parseChineseUnder10000(wanParts[1] ?? "");
  }

  return parseChineseUnder10000(s);
}

function normalizeAmountRaw(raw: string): string {
  return raw
    .trim()
    .replace(/[，,\s]/g, "")
    .replace(/整$/g, "")
    .replace(/^NT\$?/i, "")
    .replace(/^\$/g, "")
    .replace(/臺/g, "台");
}

function parseAmountInput(raw: string, opts: { defaultUnit: "wan" | "yuan" }): number {
  const normalized = normalizeAmountRaw(raw);
  if (!normalized) return 0;

  const hasComma = /[，,]/.test(raw);
  const hasWan = normalized.includes("萬");
  const hasYuan = /元$/.test(normalized);

  if (containsChineseNumberTokens(normalized)) {
    const n = parseChineseNumber(normalized.replace(/元$/g, ""));
    return Math.trunc(n);
  }

  let s = normalized;

  if (hasWan) {
    s = s.replace(/萬/g, "");
    const n = Number(s);
    return Number.isFinite(n) ? Math.trunc(n * 10_000) : 0;
  }

  if (hasYuan) {
    s = s.replace(/元$/g, "");
    const n = Number(s);
    return Number.isFinite(n) ? Math.trunc(n) : 0;
  }

  if (hasComma) {
    const n = Number(s);
    return Number.isFinite(n) ? Math.trunc(n) : 0;
  }

  const n = Number(s);
  if (!Number.isFinite(n)) return 0;
  return opts.defaultUnit === "wan" ? Math.trunc(n * 10_000) : Math.trunc(n);
}

function toStringOrEmpty(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function toArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

function toObject(v: unknown): UnknownRecord {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as UnknownRecord) : {};
}

function parseMoneyToYuan(raw: unknown, defaultUnit: "wan" | "yuan" = "wan"): number {
  if (raw == null) return 0;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    // 這裡無法知道單位，保守假設已經是「元」
    return Math.trunc(raw);
  }
  const s = String(raw).trim();
  if (!s) return 0;
  try {
    return parseAmountInput(s, { defaultUnit });
  } catch {
    return 0;
  }
}

function sum(nums: number[]): number {
  return nums.reduce((a, b) => a + b, 0);
}

type PersonData = Borrower;

function normalizePersonFromPragmatic(rawPerson: UnknownRecord, rootInput?: unknown, role?: "BORROWER" | "GUARANTOR"): PersonData {
  // Pragmatic collectData() 風格：
  // rawPerson["_income"][catId] = [{ monthly: "8.8" ... }]
  // rawPerson[catId] = [{ amount: "100萬" ... }]  (資產)
  // rawPerson["other-re"] = [{ amount: ... }]
  // rawPerson["_debt"][catId] = [{ balance, monthly ... }]

  const incomes: IncomeItem[] = [];
  const adapter = typeof window !== "undefined" ? window.BLIncomeContract : undefined;
  const canonical = adapter && rootInput && role ? adapter.getPersonIncomeSources(rootInput, role) : [];
  const categoryByType: Record<string, IncomeCategory> = { SALARY:"salary", BUSINESS:"business", PROFESSIONAL_PRACTICE:"profession", OTHER:"otherinc" };
  canonical.forEach((source) => incomes.push({ category:categoryByType[source.type] || "otherinc", monthlyYuan:source.normalizedMonthlyAmount || 0, raw:source.metadata || {} }));

  const assets: AssetItem[] = [];
  (
    [
      "deposit",
      "fixed",
      "fund",
      "bond",
      "gold",
      "stock",
      "ins",
      "misc",
    ] as AssetCategory[]
  ).forEach((cat) => {
    const items = toArray(rawPerson[cat]);
    items.forEach((it) => {
      const itemObj = toObject(it);
      assets.push({
        category: cat,
        amountYuan: parseMoneyToYuan(itemObj["amount"], "wan"),
        raw: itemObj,
      });
    });
  });

  // 名下其他不動產：Pragmatic 用 "other-re"
  const otherReItems = toArray(rawPerson["other-re"]);
  otherReItems.forEach((it) => {
    const itemObj = toObject(it);
    assets.push({
      category: "real_estate",
      amountYuan: parseMoneyToYuan(itemObj["amount"], "wan"),
      raw: itemObj,
    });
  });

  // 本案不動產（Pragmatic 的 collectData 會放在 _case.amt）
  const caseObj = toObject(rawPerson["_case"]);
  if (caseObj["amt"] != null) {
    assets.push({
      category: "real_estate",
      amountYuan: parseMoneyToYuan(caseObj["amt"], "wan"),
      raw: caseObj,
    });
  }

  const debts: DebtItem[] = [];
  const debtRoot = toObject(rawPerson["_debt"]);
  (
    [
      "mortgage",
      "carloan",
      "personal",
      "creditcard",
      "bizloan",
      "otherdebt",
    ] as DebtCategory[]
  ).forEach((cat) => {
    const items = toArray(debtRoot[cat]);
    items.forEach((it) => {
      const itemObj = toObject(it);
      debts.push({
        category: cat,
        balanceYuan: parseMoneyToYuan(itemObj["balance"], "wan"),
        monthlyYuan: parseMoneyToYuan(itemObj["monthly"], "wan"),
        raw: itemObj,
      });
    });
  });

  return { incomes, assets, debts };
}

function normalizeProperty(input: UnknownRecord): PropertyData {
  const prop = toObject(input["property"]);

  // Pragmatic 會用 propertyData: { fullAddress, ... }
  // 也可能有 caseData.property: { address }
  const fullAddress =
    toStringOrEmpty(prop["fullAddress"]) ||
    toStringOrEmpty(prop["address"]) ||
    toStringOrEmpty(prop["addr"]);

  const estimatedValueYuan =
    prop["estimatedValueYuan"] != null
      ? parseMoneyToYuan(prop["estimatedValueYuan"], "yuan")
      : // 若只有估值字串（如 "500萬"）也可以吃
        parseMoneyToYuan(prop["estimatedValue"], "wan");

  return {
    caseType: typeof prop["caseType"] === "string" ? prop["caseType"] : undefined,
    city: toStringOrEmpty(prop["city"]) || undefined,
    district: toStringOrEmpty(prop["district"]) || undefined,
    fullAddress: fullAddress || undefined,
    buildDate: toStringOrEmpty(prop["buildDate"]) || undefined,
    parking: typeof prop["parking"] === "string" ? prop["parking"] : undefined,
    nonbank: typeof prop["nonbank"] === "string" ? prop["nonbank"] : undefined,
    estimatedValueYuan: estimatedValueYuan || undefined,
  };
}

/**
 * normalizeCaseData(input)
 * 目標：把「前端零散資料」整理成統一格式 CaseData。
 * - 支援 Pragmatic collectData() 風格：{ b: {...}, g: {...}, _guarantorState }
 * - 也容許你直接傳已經接近 CaseData 的結構（此時會做最小修正）
 */
export function normalizeCaseData(input: unknown): CaseData {
  const obj = toObject(input);

  const applicant = toObject(obj["applicant"]);
  const applicantOut: Applicant = {
    name: typeof applicant["name"] === "string" ? applicant["name"] : undefined,
    idNo: typeof applicant["idNo"] === "string" ? applicant["idNo"] : undefined,
    mobile: typeof applicant["mobile"] === "string" ? applicant["mobile"] : undefined,
  };

  // Pragmatic: borrower 在 obj.b，guarantor 在 obj.g
  const bRaw = toObject(obj["b"] ?? obj["borrower"]);
  const gRaw = toObject(obj["g"] ?? obj["guarantor"]);

  const borrower = normalizePersonFromPragmatic(bRaw, obj, "BORROWER");

  const guarantorState =
    typeof obj["_guarantorState"] === "string"
      ? obj["_guarantorState"]
      : typeof obj["guarantorState"] === "string"
        ? obj["guarantorState"]
        : undefined;

  const hasGuarantorData =
    gRaw &&
    ((typeof window !== "undefined" && window.BLIncomeContract && window.BLIncomeContract.getPersonIncomeSources(obj, "GUARANTOR").length > 0) ||
      toArray(gRaw["deposit"]).length > 0 ||
      toArray(toObject(gRaw["_debt"])["mortgage"]).length > 0 ||
      Object.keys(gRaw).length > 0);

  const guarantor = hasGuarantorData ? (normalizePersonFromPragmatic(gRaw, obj, "GUARANTOR") as Guarantor) : undefined;

  const property = normalizeProperty(obj);

  return {
    applicant: applicantOut,
    property,
    borrower,
    guarantor,
    meta: {
      guarantorState,
    },
  };
}

function personTotals(p: PersonData) {
  const monthlyIncomeYuan = sum(p.incomes.map((i) => i.monthlyYuan));
  const assetYuan = sum(p.assets.map((a) => a.amountYuan));
  const monthlyDebtYuan = sum(p.debts.map((d) => d.monthlyYuan));
  const debtBalanceYuan = sum(p.debts.map((d) => d.balanceYuan));
  return { monthlyIncomeYuan, assetYuan, monthlyDebtYuan, debtBalanceYuan };
}

/**
 * buildCaseSummary(input)
 * 回傳案件摘要（總月收/總資產/總月負債/總餘額/DTI + warnings）
 */
export function buildCaseSummary(input: unknown): CaseSummary {
  const data = normalizeCaseData(input);

  const b = personTotals(data.borrower);
  const g = data.guarantor ? personTotals(data.guarantor) : null;

  const totalMonthlyIncomeYuan = b.monthlyIncomeYuan + (g?.monthlyIncomeYuan ?? 0);
  const totalAssetYuan = b.assetYuan + (g?.assetYuan ?? 0);
  const totalMonthlyDebtYuan = b.monthlyDebtYuan + (g?.monthlyDebtYuan ?? 0);
  const totalDebtBalanceYuan = b.debtBalanceYuan + (g?.debtBalanceYuan ?? 0);

  const debtRatio =
    totalMonthlyIncomeYuan > 0 ? totalMonthlyDebtYuan / totalMonthlyIncomeYuan : null;

  const warnings: string[] = [];

  if (!data.property.caseType) warnings.push("未填寫案件型態");
  if (!data.property.fullAddress) warnings.push("未填寫不動產地址");

  if (b.monthlyIncomeYuan === 0) warnings.push("借款人未填任何月收入");

  const borrowerMissingMonthly = data.borrower.debts
    .map((d, idx) => ({ d, idx }))
    .filter(({ d }) => (d.balanceYuan > 0 || d.raw) && d.monthlyYuan === 0);
  borrowerMissingMonthly.forEach(({ d, idx }) => {
    warnings.push(`借款人負債「${d.category}」第 ${idx + 1} 筆未填月付金`);
  });

  if (debtRatio !== null && debtRatio > 0.4) {
    warnings.push(`負債比偏高：${(debtRatio * 100).toFixed(1)}%`);
  }

  if (b.monthlyIncomeYuan > 2_000_000) {
    warnings.push("借款人月收入超過 200 萬，請確認填的是月收入不是年收入");
  }

  if (data.meta?.guarantorState === "invited") {
    warnings.push("保證人為邀請填寫狀態，送件前請確認資料是否已補齊");
  }

  return {
    totalMonthlyIncomeYuan,
    totalAssetYuan,
    totalMonthlyDebtYuan,
    totalDebtBalanceYuan,
    debtRatio,
    warnings,
  };
}

/**
 * buildReportSections(input)
 * 回傳報告各區塊資料（乾淨 JSON），不輸出 HTML。
 */
export function buildReportSections(input: unknown): ReportSections {
  const data = normalizeCaseData(input);

  const borrowerTotals = personTotals(data.borrower);
  const guarantorTotals = data.guarantor ? personTotals(data.guarantor) : null;
  const totals = buildCaseSummary(input);

  const sections: ReportSections = {
    meta: {
      generatedAt: new Date().toISOString(),
      guarantorIncluded: !!data.guarantor,
    },
    applicant: data.applicant,
    property: data.property,
    borrower: {
      totals: borrowerTotals,
      incomes: data.borrower.incomes,
      assets: data.borrower.assets,
      debts: data.borrower.debts,
    },
    totals,
  };

  if (data.guarantor && guarantorTotals) {
    sections.guarantor = {
      totals: guarantorTotals,
      incomes: data.guarantor.incomes,
      assets: data.guarantor.assets,
      debts: data.guarantor.debts,
    };
  }

  return sections;
}

// ----------------------------
// 最小測試資料（依需求放在檔案最下方）
// ----------------------------
const _testInput = {
  applicant: { name: "王小明", mobile: "0912345678" },
  property: { caseType: "buy", city: "台北市", district: "大安區", fullAddress: "仁愛路四段 1 號" },
  _guarantorState: "not_created",
  b: {
    _income: {
      salary: [{ company: "ABC", monthly: "8.8" }],
      business: [],
      profession: [],
      otherinc: [],
    },
    deposit: [{ bank: "台灣銀行", amount: "500萬" }],
    _case: { amt: "2000萬" },
    _debt: {
      mortgage: [{ bank: "某銀行", balance: "1000萬", monthly: "3.2" }],
      carloan: [],
      personal: [],
      creditcard: [],
      bizloan: [],
      otherdebt: [],
    },
  },
};

console.log("[reportCore] normalizeCaseData =>", normalizeCaseData(_testInput));
console.log("[reportCore] buildCaseSummary =>", buildCaseSummary(_testInput));
console.log("[reportCore] buildReportSections =>", buildReportSections(_testInput));
